import { useEffect, useState } from 'react';
import {
  FieldArrayMethodProps, useFieldArray, useForm, useWatch,
} from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import watchTriggers from '../../features/quote/data/WatchTriggers';
import {
  actions, calculateQuote, getCurrentStep, getQuote, processPayment, getCyberRemoveQueue,
} from '../../features/quote/QuoteSlice';
import { Field, WatchTriggerActionInterface, WatchTriggerInterface } from '../../types';
import { WatchTriggerActionType } from '../../types/enum';
import useClonedFields from './useClonedFields';
import useActions from './useActions';

const useQuoteJourney = () => {
  const methods = useForm({
    reValidateMode: 'onChange',
    mode: 'onChange',
  });
  const quoteData = useSelector(getQuote);
  const cyberRemoveQueue = useSelector(getCyberRemoveQueue);
  const dispatch = useDispatch();
  const { createClones } = useClonedFields();
  const stepObject = quoteData.steps[quoteData.step.step];
  const innerStepObject = stepObject.steps[quoteData.step.innerStep];
  const [loadedTriggers, addToTriggers] = useState(watchTriggers);
  const { runAction } = useActions(loadedTriggers);
  const { isValid } = methods.formState;
  const {
    fields, append, remove, insert, update,
  } = useFieldArray({
    control: methods.control,
    name: 'quote',
  });

  // check for 'back navigation' by browser & refresh data
  useEffect(() => window.addEventListener('popstate', (event) => {
    const poppedState = JSON.parse(event.state || {});
    if (Object.prototype.hasOwnProperty.call(poppedState, 'kb_quote')
      && poppedState.kb_quote.data
      && poppedState.kb_quote.step
      && poppedState.kb_quote.savedAt
    ) {
      const savedAt = new Date(poppedState.kb_quote.savedAt);
      if (savedAt.getTime() > Date.now() - 600000) {
        const quoteDataArrayed = Array.from(poppedState.kb_quote.data);
        dispatch(actions.updateQuote({
          quote: quoteDataArrayed,
        }));
        dispatch(actions.goToStep(poppedState.kb_quote.step));
      }
    }
  }), []);

  useEffect(() => {
    remove();
    append(innerStepObject.fields, {
      focusIndex: -1,
    });
    window.scrollTo(0, 0);
    if (quoteData.step.step === 0) {
      dispatch(actions.updateQuote(methods.getValues()));
    }
  }, [quoteData.step.innerStep, quoteData.step.step]);

  useEffect(() => {
    if (innerStepObject.addAfter && quoteData.step.step === 0) {
      innerStepObject.addAfter.forEach((addAfterField: string) => {
        if (Object.prototype.hasOwnProperty.call(quoteData.showData, addAfterField)) {
          dispatch(actions.addToDisplayQueue([{
            step: [quoteData.step.step, quoteData.step.innerStep],
            index: quoteData.showData[addAfterField].fieldIndex,
            trigger: '',
            fields: [
              quoteData.showData[addAfterField].field,
            ],
          }]));
        }
      });
    }
  }, [quoteData.step.innerStep, quoteData.step.step]);

  useEffect(() => {
    if (window.quickInfoData) {
      if (innerStepObject.fields.findIndex(
        (field: Field) => field.formId === window.quickInfoData.industryCtrl,
      ) > -1) {
        if (quoteData.renderClones > 0 || quoteData.numberOfFeeEarners < 2) {
          fields.forEach((f: any) => {
            if (f.parent) {
              if (f.parent === window.quickInfoData.industryCtrl
                || f.parent === window.quickInfoData.occupationCtrl) {
                dispatch(actions.addToRemoveQueue({
                  step: [quoteData.step.step, quoteData.step.innerStep],
                  fields: [f.formId],
                  trigger: window.quickInfoData.numberOfFeeEarnersCtrl,
                }));
              }
            }
          });
        }
        if (quoteData.numberOfEmployees > 1 && quoteData.numberOfFeeEarners > 1
          && quoteData.renderClones > 1) {
          createClones({
            amount: quoteData.numberOfFeeEarners,
            trigger: quoteData.numberOfFeeEarnersCtrl,
            fields: [window.quickInfoData.industryCtrl, window.quickInfoData.occupationCtrl],
          });
        }
        dispatch(actions.setNumberOfRenderClones({ value: 0 }));
      }
    }
  }, [quoteData.numberOfFeeEarners, quoteData.renderClones]);

  useEffect(() => {
    const numberOfEmployees = parseInt(quoteData.numberOfEmployees, 2);

    /**
     * Add the trigger to the queue to maintain its state, remove matching trigger
     * from the loadedTriggers object, used runAction to recall useWatchTriggers,
     * which checks the triggers once more (after changing).
     *
     * @param field Field
     * @param trigger Trigger
     * @param index Trigger index
     */
    function removeMatchingTriggerAndRefresh(field: any, trigger: any, index: number) {
      // Add trigger into remove queue
      dispatch(actions.addTriggerToRemoveQueue({ prop: field.formId, trigger }));
      // Remove matching trigger
      loadedTriggers[field.formId].splice(index, 1);
      // Replay checkForTriggers
      runAction(field.formId, trigger.on, field.value, index + trigger.index);
    }

    /**
     * Search for matching trigger in delete queue. If matches, add trigger
     * back into loadedTriggers object so that it does not conflict with another
     * Cyber trigger in 'proposal_question_visibility'.
     *
     * @param trigger Name of the question e.g. quote_prop_43/quote_prop_76
     */
    function restoretMatchingTriggerFromDeleteQueue(trigger: string) {
      Object.keys(cyberRemoveQueue).forEach((key: any) => {
        const value = cyberRemoveQueue[key];
        if (key === trigger) {
          dispatch(actions.removeTriggerFromRemoveQueue(trigger));
          loadedTriggers[key].push(value);
        }
      });
    }

    /**
     * Loops fields searching for trigger matching Cyber Liability question
     *
     * Solution was setup as there are now conflicting rows matching the question -
     * feeEarners => 1 (1085) both on:show and off:hide, interferes with new rejected occupations.
     *
     * Implementing a priority system is not adequate as both Fee Earner and rejected
     * occupations have equal importance.
     */
    fields.forEach((field: any): void => {
      // Cancel in the event of issue/mismatch
      if (!field.formId || !loadedTriggers[field.formId]) return;
      // Loop loaded triggers
      loadedTriggers[field.formId].forEach((trigger: any, index: number) => {
        // Checks proposal_question_visibility for matching quote_prop_89
        if (trigger.on.target
          && trigger.on.target.includes(window.quickInfoData.cyberLiabilityCtrl)) {
          if (quoteData.numberOfFeeEarners < 2 || numberOfEmployees === 1) {
            switch (trigger.visibility) {
              case 'show':
                removeMatchingTriggerAndRefresh(field, trigger, index);
                break;
              default:
                break;
            }
          } else {
            // eslint-disable-next-line no-lonely-if
            if (trigger.visibility === 'hide') {
              removeMatchingTriggerAndRefresh(field, trigger, index);
            }
          }
        }
      });
    });

    // Check for matching 'deleted' trigger, re-introduce into loadedTriggers object
    if (Object.keys(cyberRemoveQueue).length > 0) {
      if (quoteData.numberOfFeeEarners < 2) {
        restoretMatchingTriggerFromDeleteQueue(window.quickInfoData.occupationCtrl);
      } else {
        restoretMatchingTriggerFromDeleteQueue(window.quickInfoData.numberOfFeeEarnersCtrl);
      }
    }
  }, [quoteData.numberOfEmployees, quoteData.numberOfFeeEarners, quoteData.cyberLiabilityCtrl]);

  useEffect(() => {
    if (window.quickInfoData && quoteData.step.step === 0 && quoteData.step.innerStep === 2) {
      if (quoteData.numberOfOffShore > 0 && quoteData.numberOfFeeEarners > 1) {
        // Add SME oil to view
        dispatch(actions.addToDisplayQueue([{
          step: [quoteData.step.step, quoteData.step.innerStep],
          index: quoteData.showData[window.quickInfoData.smeOffshoreWorkersControl].index,
          trigger: window.quickInfoData.industryCtrl,
          fields: [
            quoteData.showData[window.quickInfoData.smeOffshoreWorkersControl].field,
          ],
        }]));
      } else {
        dispatch(actions.addToRemoveQueue(
          {
            step: [quoteData.step.step, quoteData.step.innerStep],
            trigger: window.quickInfoData.industryCtrl,
            fields: [window.quickInfoData.smeOffshoreWorkersControl],
          },
        ));
      }
    }
  }, [quoteData.numberOfOffShore, quoteData.numberOfFeeEarners, quoteData.step.innerStep]);

  useEffect(() => {
    /**
     * Hide Cyber if 'cyber_active_date' value is older than inception date.
     * Also, Hide Cyber if cyberLiabilityActiveDate is null ('cyber_active_date' has expired)
     */
    if (quoteData.step.step === 0 && quoteData.step.innerStep === 3) {
      const inceptionDate = new Date(quoteData.inceptionDate);
      const cyberActiveDate = new Date(window.quickInfoData.cyberLiabilityActiveDate);
      if (inceptionDate < cyberActiveDate
        || window.quickInfoData.cyberLiabilityActiveDate === null) {
        dispatch(actions.addToRemoveQueue({
          step: [quoteData.step.step, quoteData.step.innerStep],
          trigger: 'more_inception',
          fields: ['quote_prop_89'],
        }));
      }
    }
  }, [quoteData.step.innerStep, quoteData.inceptionDate]);

  useEffect(() => {
    const totalNumberOfStaff = Number(quoteData.numberOfFeeEarners)
      + Number(quoteData.numberOfAdmin)
      + Number(quoteData.numberOfNonAdmin);
    dispatch(actions.setTotalNumberOfStaff(totalNumberOfStaff));
  }, [
    quoteData.numberOfFeeEarners,
    quoteData.numberOfEmployees,
    quoteData.numberOfAdmin,
    quoteData.numberOfNonAdmin,
  ]);

  useEffect(() => {
    if (quoteData.paymentIframeHtml !== null) {
      dispatch(actions.setIsLoading(true));
      dispatch(actions.setLoadingText(quoteData.paymentIframeHtml));
    }
  }, [quoteData.paymentIframeHtml, dispatch]);

  useEffect(() => {
    const removeArray: number[] = [];
    if (quoteData.removingQueue && quoteData.removingQueue.length > 0) {
      // Need to check if this field has any hide triggers if it does
      fields.forEach((field: any, index: any) => {
        const objFunc = Object.prototype.hasOwnProperty.call(watchTriggers, field.formId);
        if (quoteData.removingQueue.indexOf(field.formId) > -1) {
          removeArray.push(index);
          if (Object.prototype.hasOwnProperty.call(watchTriggers, field.formId)) {
            watchTriggers[field.formId].forEach((watchTrigger: WatchTriggerInterface) => {
              if (watchTrigger.on.type === WatchTriggerActionType.Show
                || watchTrigger.off.type === WatchTriggerActionType.Show) {
                const removeId = watchTrigger.on.type
                  ? watchTrigger.on.target
                  : watchTrigger.off.target;
                dispatch(actions.addToRemoveQueue({
                  step: [quoteData.step.step, quoteData.step.innerStep],
                  fields: removeId,
                  trigger: field.formId,
                }));
              }
            });
          }
        }
      });

      dispatch(actions.clearRemoving());
      if (removeArray.length > 0) {
        remove(removeArray);
      }
    }
    dispatch(actions.setIsRemoving(false));
  }, [quoteData.removingQueue]);

  useEffect(() => {
    if (quoteData.formQueue && quoteData.formQueue.length) {
      let insertOffset = -1;
      let offset = true;
      quoteData.formQueue.forEach((queue: any, queueIndex: number) => {
        let insertAtIndex = queue.index;
        // If sort order 5. or 6. we need to intervene
        // This is due to the layout differences on QJ1 & QJ2
        queue.fields.forEach((queueField: Field, fieldIndex: number) => {
          if (queueField.sort) {
            insertAtIndex = fields.findIndex(
              (field: any) => {
                if (field.sort) {
                  const searchSort = (field.sort.indexOf('5.') > -1 || field.sort.indexOf('6.') > -1)
                    ? '4.11'
                    : field.sort;
                  return searchSort > queueField.sort;
                }
                return false;
              },
            );
            if (insertAtIndex === -1) {
              insertAtIndex = fields.length;
            }
          }
          if (queueField.parent) {
            insertAtIndex = fields.findIndex(
              (field: any) => field.formId === queueField.parent,
            ) + 2;
            offset = false;
          } else {
            offset = true;
          }
        });
        const insertFields = queue.fields.filter(
          (queueField: Field) => fields.findIndex(
            (field: any) => queueField.formId === field.formId,
          ) === -1 && queue.step[1] === quoteData.step.innerStep,
        );
        if (insertFields.length > 0) {
          if (quoteData.step.step === 0 && quoteData.step.innerStep === 2) {
            insertOffset += offset ? 1 : 0;
          } else if (quoteData.step.step !== 2) {
            insertOffset += 1;
          } else {
            insertOffset += quoteData.formQueue.length > 2
              ? queue.fields.length : 1;
          }
          insert(insertAtIndex + insertOffset, insertFields, {
            shouldFocus: false,
          });
          insertOffset += offset ? 0 : queue.fields.length;
        }
      });
      dispatch(actions.clearFormQueue());
    }

    dispatch(actions.setIsDrawing(false));

    setTimeout(() => {
      dispatch(actions.setIsLoading(false));
    }, 500);
  }, [quoteData.formQueue]);

  useEffect(() => {
    if (quoteData.updateQueue && quoteData.updateQueue.length) {
      quoteData.updateQueue.forEach((action: WatchTriggerActionInterface) => {
        fields.forEach((field: any, index: number) => {
          if (action.attribute) {
            if (action.target.indexOf(field.formId) > -1) {
              update(index, {
                ...field,
                [action.attribute.key]: action.attribute.value ? action.attribute.value : '',
              });
            }
          }
        });
      });

      dispatch(actions.clearUpdateQueue());
    }

    setTimeout(() => {
      dispatch(actions.setIsLoading(false));
      dispatch(actions.setIsUpdating(false));
    }, 500);
  }, [quoteData.updateQueue]);

  // hide the IR35 Protect add-on option when the partner is Paystream
  useEffect(() => {
    if (quoteData.step.step === 0
      && quoteData.step.innerStep === 3
      && (
        (quoteData.quote.json?.partner?.code
          && ['PAYST_IYDCE', 'PAYST_24112', 'PAYST_01033'].includes(quoteData.quote.json.partner.code)
          // this test works the first time through the questions
        )
        || (quoteData.data.pid
          && ['786', '1874', '1947'].includes(quoteData.data.pid)
          // this test works after using the 'Change options' button
        )
      )
    ) {
      // I'm sure this isn't the React way of doing things, but it works.
      setTimeout(() => {
        const el = document.getElementById('quote_prop_100-2');
        if (el) {
          const container = el.parentElement?.parentElement?.parentElement?.parentElement;
          el.parentElement?.parentElement?.remove();
          // also remove 'IR35 Protect' from the question wording, and change the tooltip
          if (container) {
            const label = container.firstElementChild?.firstElementChild;
            if (label) {
              label.innerHTML = 'Do you want to add Legal Expenses cover?';
            }
            const tooltips = Array.from(container.getElementsByTagName('small'));
            // there will only be one anyway
            tooltips.forEach((tooltip) => {
              tooltip.setAttribute(
                'title',
                'Legal Expenses cover includes legal and professional defence costs for IR35 investigations, plus much more.',
              );
              tooltip.setAttribute(
                'data-bs-original-title',
                'Legal Expenses cover includes legal and professional defence costs for IR35 investigations, plus much more.',
              );
            });
          }
        }
      }, 1000);
    }
  }, [quoteData.step.step, quoteData.step.innerStep, quoteData.quote.json?.partner?.code]);

  const submitToServer = (values?: any) => {
    // //Send data to server
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '';

    const hidden = document.createElement('input');
    hidden.type = 'hidden';
    hidden.name = 'quote_submit';
    hidden.value = 'Continue >';
    form.appendChild(hidden);

    const submitValues = values || quoteData.data;
    Object.keys(submitValues).forEach((submitKey: string) => {
      if (submitValues[submitKey]) {
        if (typeof submitValues[submitKey] === 'object') {
          if (window.quickInfoData
            && submitKey === window.quickInfoData.ernNumberCtrl) {
            Object.keys(submitValues[submitKey]).forEach((key: string) => {
              const hiddenField = document.createElement('input');
              hiddenField.type = 'hidden';
              hiddenField.name = key;
              hiddenField.value = submitValues[submitKey][key];

              form.appendChild(hiddenField);
            });

            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = submitKey;
            hiddenField.value = Object.values(submitValues[submitKey]).join('/');
            form.appendChild(hiddenField);
          }
        } else {
          const hiddenField = document.createElement('input');
          hiddenField.type = 'hidden';
          hiddenField.name = submitKey;
          hiddenField.value = submitValues[submitKey];

          form.appendChild(hiddenField);
        }
      }
    });

    const hiddenField = document.createElement('input');
    hiddenField.type = 'hidden';
    hiddenField.name = 'ptc';
    hiddenField.value = window.kbSchema.transaction.ptc;
    form.appendChild(hiddenField);

    document.body.appendChild(form);
    form.submit();
  };

  const nextStep = async (e: any) => {
    dispatch(actions.setMessage(null));
    if (await methods.trigger('quote', { shouldFocus: true }) || Object.keys(methods.formState.errors).length === 0) {
      await dispatch(actions.updateQuote(methods.getValues()));
      dispatch(actions.setIsLoading(true));
      if (quoteData.step.step === 0 && quoteData.step.innerStep === 0) {
        const data = new FormData();
        data.append('email', quoteData.data.quote_email);
        data.append('forename', quoteData.data.quote_forename);
        data.append('surname', quoteData.data.quote_surname);
        data.append('phone', quoteData.data.quote_phone);

        // add the 'My friend' data, if present
        if (quoteData.data.info_where_extra_questions_19_28 !== undefined) {
          data.append('referred_by_name', quoteData.data.info_where_extra_questions_19_28);
        }
        if (quoteData.data.info_where_extra_questions_19_30 !== undefined) {
          data.append('referred_by_email', quoteData.data.info_where_extra_questions_19_30);
        }

        // add the 'My employer/client' data, if present
        let employerReferrer = '';
        if (quoteData.data.info_where_extra_questions_18_27 !== undefined) {
          employerReferrer = quoteData.data.info_where_extra_questions_18_27;
        }
        if (quoteData.data.info_where_extra_questions_18_33 !== undefined) {
          if (employerReferrer === '') {
            employerReferrer = quoteData.data.info_where_extra_questions_18_33;
          } else {
            employerReferrer = `${employerReferrer} - ${quoteData.data.info_where_extra_questions_18_33}`;
          }
        }
        if (employerReferrer !== '') {
          data.append('referred_by_name', employerReferrer);
        }

        /*
        Annoyingly, the info_where field gets used for different things depending on the situation.
        We need to know whether it's a <select> or and <input type="text"> to decide which field
        to put its data into.
         */
        if (quoteData.showData.info_where !== undefined) {
          if (quoteData.showData.info_where.field.type === 'select') {
            data.append('info_where', quoteData.data.info_where);
          } else {
            data.append('referred_by_name', quoteData.data.info_where);
          }
        }

        /*
        Normally, quoteData.quote.json.partner IS defined, giving us the name and code attributes.
        But if the customer has used the 'Change options' button, we only have quotedata.data.pid,
        and we'll need to look up the partner details in KCI.
         */
        if (quoteData.quote.json.partner !== undefined) {
          data.append('partner_name', quoteData.quote.json.partner.name);
          data.append('partner_code', quoteData.quote.json.partner.code);
          data.append('partner_business_type', quoteData.quote.json.partner.business_type);
        }
        if (quoteData.data.pid !== undefined) {
          data.append('partner_id', quoteData.data.pid);
        }

        data.append('ptc', window.kbSchema.transaction.ptc);

        /**
         * Need to check if inital occupation is online or offline
         * If it's offline then we can trigger our endQoute and trigger
         * salesforce lead
         */
        if (quoteData.data.occupation?.includes('Offline')) {
          data.append('occupation', quoteData.data.occupation);
          return axios.post('/buy/ajax/send_lead_to_salesforce.html', data)
            .then(() => {
              submitToServer();
            });
        }

        axios.post('/buy/ajax/send_lead_to_salesforce.html', data);
      }
      if (quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action) {
        if (quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action === 'quote') {
          dispatch(calculateQuote(quoteData.data));
          return true;
        }
        if (quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action === 'important'
          || quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action === 'about'
          || quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action === 'payment') {
          submitToServer();
          return true;
        }
        if (quoteData.steps[quoteData.step.step].steps[quoteData.step.innerStep].action === 'process') {
          // Unless it's direct debit submit to server
          if (quoteData.data.payment_paytype === 'directdebit') {
            submitToServer();
          } else {
            dispatch(processPayment({
              ...methods.getValues(),
              ptc: window.kbSchema.transaction.ptc,
            }));
          }
          return true;
        }
      }
      dispatch(actions.goToNextStep());
    }

    e.preventDefault();
    return false;
  };

  const previousStep = () => {
    dispatch(actions.goToPrevStep());
  };
  const goToStep = () => {
    //
  };
  const reset = () => {
    //
  };

  const goToStart = (e: any) => {
    dispatch(actions.goToStep({
      innerStep: 0,
      step: 0,
    }));
    e.preventDefault();
  };
  return {
    nextStep,
    previousStep,
    goToStep,
    append,
    reset,
    fields,
    methods,
    submitToServer,
    goToStart,
  };
};

export default useQuoteJourney;
